import { ApiService } from './api';

// Types for Project Manager Dashboard
export interface PMProject {
  id: number;
  name: string;
  client: string;
  developer?: string;
  status: string;
  progress: number;
  budget?: string;
  priority?: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PMMessage {
  id: number;
  client: string;
  clientEmail?: string;
  subject?: string;
  message: string;
  time: string;
  unread: boolean;
  priority: 'low' | 'medium' | 'high';
  projectId?: number;
}

export interface PMStats {
  activeProjects: number;
  totalClients: number;
  pendingMessages: number;
  monthlyRevenue: string;
  completedThisMonth?: number;
  onTimeDelivery?: string;
  clientSatisfaction?: string;
  teamUtilization?: string;
  avgProjectDuration?: string;
  budgetUtilization?: string;
}

export interface PMDashboardData {
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  projects: PMProject[];
  messages: PMMessage[];
  stats: PMStats;
}

/**
 * Project Manager Dashboard API Service
 * Handles all API calls related to project manager dashboard functionality
 */
export class ProjectManagerDashboardService {
  /**
   * Get complete project manager dashboard data
   */
  static async getDashboardData(): Promise<PMDashboardData> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: PMDashboardData;
      }>('/project-manager/dashboard');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project manager dashboard data:', error);
      throw new Error('Failed to load project manager dashboard data');
    }
  }

  /**
   * Get project manager's projects with optional filtering
   */
  static async getProjects(filters?: {
    status?: string;
    client?: string;
    priority?: string;
  }): Promise<PMProject[]> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: PMProject[];
        meta: {
          total: number;
        };
      }>('/project-manager/projects', { params: filters });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project manager projects:', error);
      throw new Error('Failed to load projects');
    }
  }

  /**
   * Get project manager's messages with optional filtering
   */
  static async getMessages(filters?: {
    unread?: boolean;
    priority?: string;
    client?: string;
  }): Promise<PMMessage[]> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: PMMessage[];
        meta: {
          total: number;
          unreadCount: number;
        };
      }>('/project-manager/messages', { params: filters });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project manager messages:', error);
      throw new Error('Failed to load messages');
    }
  }

  /**
   * Get project manager statistics
   */
  static async getStats(): Promise<PMStats> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: PMStats;
      }>('/project-manager/stats');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project manager stats:', error);
      throw new Error('Failed to load statistics');
    }
  }

  /**
   * Mark a message as read
   */
  static async markMessageAsRead(messageId: number): Promise<void> {
    try {
      await ApiService.patch(`/project-manager/messages/${messageId}/read`);
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw new Error('Failed to mark message as read');
    }
  }

  /**
   * Send a message to a client
   */
  static async sendMessage(messageData: {
    client_id: number;
    subject: string;
    message: string;
    priority?: 'low' | 'medium' | 'high';
  }): Promise<{ id: number; sent_at: string }> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: {
          id: number;
          sent_at: string;
        };
      }>('/project-manager/messages', messageData);
      
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw new Error('Failed to send message');
    }
  }

  /**
   * Get project details
   */
  static async getProject(projectId: number): Promise<PMProject> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: PMProject;
      }>(`/project-manager/projects/${projectId}`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw new Error('Failed to load project details');
    }
  }

  /**
   * Update project status
   */
  static async updateProjectStatus(projectId: number, status: string, notes?: string): Promise<void> {
    try {
      await ApiService.patch(`/project-manager/projects/${projectId}/status`, { 
        status, 
        notes 
      });
    } catch (error) {
      console.error('Error updating project status:', error);
      throw new Error('Failed to update project status');
    }
  }

  /**
   * Update project progress
   */
  static async updateProjectProgress(projectId: number, progress: number): Promise<void> {
    try {
      await ApiService.patch(`/project-manager/projects/${projectId}/progress`, { 
        progress 
      });
    } catch (error) {
      console.error('Error updating project progress:', error);
      throw new Error('Failed to update project progress');
    }
  }

  /**
   * Assign developer to project
   */
  static async assignDeveloper(projectId: number, developerId: number, data?: {
    role?: string;
    assigned_hours?: number;
    hourly_rate?: number;
    notes?: string;
  }): Promise<void> {
    try {
      await ApiService.post(`/project-manager/projects/${projectId}/assign-developer`, {
        developer_id: developerId,
        ...data
      });
    } catch (error) {
      console.error('Error assigning developer:', error);
      throw new Error('Failed to assign developer');
    }
  }

  /**
   * Get team members available for assignment
   */
  static async getAvailableDevelopers(): Promise<Array<{
    id: number;
    name: string;
    email: string;
    skills?: string[];
    availability?: string;
  }>> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: Array<{
          id: number;
          name: string;
          email: string;
          skills?: string[];
          availability?: string;
        }>;
      }>('/project-manager/available-developers');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching available developers:', error);
      throw new Error('Failed to load available developers');
    }
  }

  /**
   * Get unread message count
   */
  static async getUnreadMessageCount(): Promise<number> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          unreadCount: number;
        };
      }>('/project-manager/messages/unread-count');
      
      return response.data.unreadCount;
    } catch (error) {
      console.error('Error fetching unread message count:', error);
      return 0;
    }
  }

  /**
   * Get team statistics
   */
  static async getTeamStats(): Promise<{
    totalMembers: number;
    activeTasks: number;
    completedTasks: number;
    averageProductivity: number;
    onlineMembers: number;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          totalMembers: number;
          activeTasks: number;
          completedTasks: number;
          averageProductivity: number;
          onlineMembers: number;
        };
      }>('/project-manager/team/stats');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching team stats:', error);
      throw new Error('Failed to load team statistics');
    }
  }

  /**
   * Get team performance metrics
   */
  static async getTeamPerformance(timeframe?: 'week' | 'month' | 'quarter'): Promise<{
    completionRate: number;
    averageTaskTime: number;
    onTimeDelivery: number;
    memberPerformance: Array<{
      memberId: number;
      name: string;
      tasksCompleted: number;
      productivity: number;
    }>;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          completionRate: number;
          averageTaskTime: number;
          onTimeDelivery: number;
          memberPerformance: Array<{
            memberId: number;
            name: string;
            tasksCompleted: number;
            productivity: number;
          }>;
        };
      }>('/project-manager/team/performance', { params: { timeframe } });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching team performance:', error);
      throw new Error('Failed to load team performance metrics');
    }
  }

  /**
   * Get project analytics
   */
  static async getProjectAnalytics(filters?: {
    timeframe?: 'week' | 'month' | 'quarter' | 'year';
    project_id?: string;
  }): Promise<{
    metrics: {
      totalProjects: number;
      activeProjects: number;
      completedProjects: number;
      onTimeDelivery: number;
      budgetUtilization: number;
      teamProductivity: number;
      clientSatisfaction: number;
      avgProjectDuration: number;
    };
    projects: Array<{
      id: number;
      name: string;
      status: string;
      progress: number;
      budget: number;
      spent: number;
      teamSize: number;
      daysRemaining: number;
      tasksCompleted: number;
      totalTasks: number;
      clientRating: number;
    }>;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: any;
      }>('/project-manager/analytics', { params: filters });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project analytics:', error);
      throw new Error('Failed to load project analytics');
    }
  }
}

export default ProjectManagerDashboardService;